import type { AssetCategory } from './simplifiedViewCategories'

/** Tallest a media card preview may render in the Simplified node list (px). */
export const LIST_PREVIEW_MAX_HEIGHT = 280
/** Node-list pane width bounds and default (default matches the old fixed `w-64`). */
export const LIST_MIN_WIDTH = 240
export const LIST_MAX_WIDTH = 960
export const LIST_DEFAULT_WIDTH = 256
/** The detail pane never shrinks below this when the list is widened or the window narrows. */
export const DETAIL_MIN_WIDTH = 420
/** Fixed chrome beside the list: the category rail (`w-20`) and the divider (`w-1.5`). */
export const RAIL_WIDTH = 80
export const DIVIDER_WIDTH = 6
/**
 * Grid track minimum; the browser adds columns as the pane widens. `auto-fit`
 * collapses empty tracks, so a category with few cards lets them use the width.
 */
export const LIST_COLUMN_MIN = 220
export const LIST_WIDTH_STORAGE_KEY = 'simplified-node-list-width'

/**
 * Categories whose cards are dominated by media with an intrinsic aspect ratio.
 * Only these get the shared height cap — text-heavy cards (script, sound,
 * timeline) would otherwise shrink to an unreadable scale.
 */
const HEIGHT_BOUNDED_CATEGORIES: ReadonlySet<AssetCategory> = new Set([
  'character', 'location', 'object', 'storyboard', 'video', 'image',
])

export function isHeightBoundedCategory(category: AssetCategory | null): boolean {
  return category !== null && HEIGHT_BOUNDED_CATEGORIES.has(category)
}

export type ListPreviewBound = 'width' | 'height' | 'native'

export interface ListPreviewFit {
  scale: number
  bound: ListPreviewBound
}

/**
 * "Contain" fit of a card's native box into the preview box: one uniform scale
 * (no stretching), never above 1 (no upscaling), and the scaled card is always
 * fully inside the box (no cropping). `boxH === null` means width-only fitting.
 */
export function fitListPreview({ nativeW, nativeH, boxW, boxH }: {
  nativeW: number
  nativeH: number
  boxW: number
  boxH: number | null
}): ListPreviewFit {
  if (nativeW <= 0 || nativeH <= 0) return { scale: 1, bound: 'native' }
  const widthScale = Math.max(1, boxW) / nativeW
  const heightScale = boxH === null ? Infinity : Math.max(1, boxH) / nativeH
  const scale = Math.min(1, widthScale, heightScale)
  if (scale === 1) return { scale, bound: 'native' }
  return { scale, bound: heightScale < widthScale ? 'height' : 'width' }
}

export interface ListWidthBounds {
  min: number
  max: number
}

/**
 * Live bounds for the node-list pane. `rootWidth === null` (not yet measured)
 * applies only the static max. When the window is too narrow to honour both the
 * list min and the detail min, the list min wins.
 */
export function listWidthBounds(rootWidth: number | null): ListWidthBounds {
  const roomForList = rootWidth === null
    ? LIST_MAX_WIDTH
    : rootWidth - RAIL_WIDTH - DIVIDER_WIDTH - DETAIL_MIN_WIDTH
  return { min: LIST_MIN_WIDTH, max: Math.max(LIST_MIN_WIDTH, Math.min(LIST_MAX_WIDTH, roomForList)) }
}

export function clampListWidth(preferred: number, bounds: ListWidthBounds): number {
  return Math.round(Math.min(Math.max(preferred, bounds.min), bounds.max))
}

/** Persisted preference, clamped to static bounds; anything unparseable → default. */
export function readStoredListWidth(): number {
  try {
    const raw = window.localStorage.getItem(LIST_WIDTH_STORAGE_KEY)
    const parsed = raw === null ? NaN : Number(raw)
    if (!Number.isFinite(parsed)) return LIST_DEFAULT_WIDTH
    return clampListWidth(parsed, listWidthBounds(null))
  } catch {
    return LIST_DEFAULT_WIDTH
  }
}

export function writeStoredListWidth(width: number): void {
  try {
    window.localStorage.setItem(LIST_WIDTH_STORAGE_KEY, String(Math.round(width)))
  } catch {
    // storage unavailable (private mode / quota) — the width stays session-only
  }
}

/**
 * Number of occupied tracks in an `auto-fit` grid. Collapsed (empty) tracks
 * resolve to `0px` in the computed value and are not counted.
 */
export function countGridColumns(el: Element): number {
  const tracks = window.getComputedStyle(el).gridTemplateColumns
  if (!tracks || tracks === 'none') return 1
  return Math.max(1, tracks.trim().split(/\s+/).filter((track) => track !== '0px').length)
}
